// ============================================================================
// Canonical Cattle Batch detail route contract
// ============================================================================
//
// The historical CattleBatchesRouter accepts a broad /cattle/batches/* space:
// its forecast projection owns /forecast/<month>, while every other non-empty
// suffix reached the persisted-record page through the historical router. This
// narrow predicate is deliberately independent of either renderer and grants a
// released render claim only to the one reviewed record shape.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX = '/cattle/batches/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

/**
 * Accept exactly one meaningful, non-reserved raw record-id segment. Decode
 * before final validation so an encoded separator, NUL, line break, dot
 * segment, or forecast namespace cannot widen this released record capability.
 */
export function isCanonicalCattleBatchesDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }

  const rawId = pathname.slice(CATTLE_BATCHES_DETAIL_PATHNAME_PREFIX.length);
  if (
    rawId === '' ||
    rawId.includes('/') ||
    rawId.includes('\\') ||
    rawId.includes('\0') ||
    rawId.includes('\r') ||
    rawId.includes('\n') ||
    rawId.includes('?') ||
    rawId.includes('#')
  ) {
    return false;
  }

  try {
    const id = decodeURIComponent(rawId);
    return (
      id.trim() !== '' &&
      id !== 'forecast' &&
      id !== '.' &&
      id !== '..' &&
      !id.includes('/') &&
      !id.includes('\\') &&
      !id.includes('\0') &&
      !id.includes('\r') &&
      !id.includes('\n') &&
      !id.includes('?') &&
      !id.includes('#')
    );
  } catch {
    return false;
  }
}

/**
 * React Router supplies pathname/search/hash separately. A bare string is
 * accepted only when it is plainly a pathname, so test seams and callers
 * cannot smuggle an unsupported query or fragment into the released target.
 */
export function isCanonicalCattleBatchesDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalCattleBatchesDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalCattleBatchesDetailPathname(pathname)
  );
}
